import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { CheckCircle, Star, Zap, Award, ArrowRight } from 'lucide-react';

const PLANS = [
  {
    id: 'free', name: 'Free Plan', price: 0, duration: '30 days', color: 'slate',
    icon: CheckCircle, iconColor: 'text-slate-500', badgeColor: 'bg-slate-100 text-slate-600',
    features: ['Basic listing', 'Standard search ranking', '1 month duration', 'Basic analytics'],
    cta: 'Get Started Free', popular: false
  },
  {
    id: 'basic', name: 'Basic Boost', price: 499, duration: '60 days', color: 'blue',
    icon: Zap, iconColor: 'text-blue-600', badgeColor: 'bg-blue-100 text-blue-700',
    features: ['Higher search ranking', '2 months duration', 'Email alerts to tenants', 'Performance analytics', 'Boost badge'],
    cta: 'Choose Basic', popular: false
  },
  {
    id: 'premium', name: 'Premium Boost', price: 999, duration: '90 days', color: 'purple',
    icon: Star, iconColor: 'text-purple-600', badgeColor: 'bg-purple-100 text-purple-700',
    features: ['Top search results', '3 months duration', 'Highlighted listing', 'Priority support', 'Detailed analytics', 'Premium badge'],
    cta: 'Choose Premium', popular: true
  },
  {
    id: 'featured', name: 'Featured Listing', price: 1999, duration: '180 days', color: 'amber',
    icon: Award, iconColor: 'text-amber-600', badgeColor: 'bg-amber-100 text-amber-700',
    features: ['Homepage visibility', '6 months duration', 'Featured badge', 'Maximum exposure', 'Dedicated support', 'Weekly performance reports'],
    cta: 'Go Featured', popular: false
  },
];

const COLOR_MAP = {
  slate: { border: 'border-slate-200', hover: 'hover:border-slate-300', btn: 'bg-slate-800 hover:bg-slate-900 text-white' },
  blue: { border: 'border-blue-200', hover: 'hover:border-blue-400', btn: 'bg-blue-700 hover:bg-blue-800 text-white' },
  purple: { border: 'border-purple-300', hover: 'hover:border-purple-500', btn: 'bg-purple-700 hover:bg-purple-800 text-white' },
  amber: { border: 'border-amber-300', hover: 'hover:border-amber-500', btn: 'bg-amber-500 hover:bg-amber-600 text-white' },
};

export default function BoostPlansPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleSelectPlan = (plan) => {
    if (!user) { navigate('/login'); return; }
    if (plan.price === 0) {
      navigate('/add-property');
    } else {
      toast.info(`${plan.name} plan selected! Contact admin to activate your paid boost.`, { duration: 5000 });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Boost Your Listing | Rent Mitra Pricing Plans</title>
        <meta name="description" content="Choose a plan to boost your property listing on Rent Mitra. Free, Basic, Premium and Featured plans available for owners and brokers." />
        <link rel="canonical" href={`${window.location.origin}/plans`} />
        <meta property="og:title" content="Boost Your Listing | Rent Mitra Pricing Plans" />
        <meta property="og:description" content="Increase your property's visibility. Plans from Free to Featured to attract quality tenants faster." />
        <meta property="og:url" content={`${window.location.origin}/pricing`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Rent Mitra" />
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <span className="inline-block bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-5 uppercase tracking-wider">Boost Your Listing</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-5" style={{fontFamily:'Outfit,sans-serif'}}>
            Get More Tenants Faster
          </h1>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Choose the right plan to increase your property's visibility and attract quality tenants quicker.
          </p>
        </div>
      </section>

      {/* Plans Grid */}
      <section className="pb-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="pricing-plans">
            {PLANS.map((plan) => {
              const c = COLOR_MAP[plan.color];
              return (
                <div
                  key={plan.id}
                  className={`relative bg-white rounded-3xl border-2 p-7 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${c.border} ${c.hover} ${plan.popular ? 'shadow-lg' : 'shadow-sm'}`}
                  data-testid={`plan-${plan.id}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-700 text-white text-xs font-bold px-4 py-1.5 rounded-full">
                      Most Popular
                    </div>
                  )}
                  {/* Header */}
                  <div className="mb-5">
                    <div className={`w-11 h-11 rounded-2xl flex items-center justify-center mb-4 ${plan.badgeColor}`}>
                      <plan.icon className={`w-5 h-5 ${plan.iconColor}`} />
                    </div>
                    <div className="font-bold text-slate-900 text-lg mb-1" style={{fontFamily:'Outfit,sans-serif'}}>{plan.name}</div>
                    <div className="flex items-end gap-1">
                      <span className="text-3xl font-bold text-slate-900">
                        {plan.price === 0 ? 'Free' : `₹${plan.price.toLocaleString('en-IN')}`}
                      </span>
                      {plan.price > 0 && <span className="text-slate-400 text-sm mb-1">/{plan.duration}</span>}
                    </div>
                    {plan.price === 0 && <div className="text-slate-400 text-sm">for {plan.duration}</div>}
                  </div>

                  {/* Features */}
                  <ul className="space-y-2.5 mb-7">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                        <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${plan.iconColor}`} />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleSelectPlan(plan)}
                    className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors ${c.btn}`}
                    data-testid={`select-plan-${plan.id}`}
                  >
                    {plan.cta} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Note */}
          <div className="mt-12 text-center">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 max-w-xl mx-auto" data-testid="payment-note">
              <p className="text-amber-800 text-sm font-medium mb-1">Payment Integration Coming Soon</p>
              <p className="text-amber-700 text-xs">
                Paid plans are currently managed manually by our admin team. Contact us at <strong>info@rentmitrahub.com</strong> to upgrade your listing. We will activate your plan within 24 hours.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
